import { createContext, useContext } from 'react';

// Context to share dropdown state with child components
export interface DropdownContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isMobile: boolean;
  closeOnItemClick: boolean;
  handleClose: () => void;
}

export const DropdownContext = createContext<DropdownContextType | null>(null);

export const useDropdownContext = () => {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('useDropdownContext must be used within a Dropdown component');
  }
  return context;
};

export interface DropdownPosition {
  top: number;
  left: number;
}

export interface DropdownProps {
  // The element that triggers the dropdown
  trigger: React.ReactNode;

  // Whether the dropdown is open (controlled mode)
  open?: boolean;

  // Default open state (uncontrolled mode)
  defaultOpen?: boolean;

  // Callback when open state changes
  onOpenChange?: (isOpen: boolean) => void;

  // Position relative to trigger
  position?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';

  // Offset from trigger position
  offset?: { x: number; y: number };

  // Width for dropdown (number for pixels, string for custom value like 'auto' or '100%')
  width?: number | string;

  // Whether to disable automatic position optimization
  disablePositionOptimization?: boolean;

  // Whether dropdown is being rendered on mobile
  isMobile?: boolean;

  // Custom class for dropdown content
  contentClassName?: string;

  // Whether to show an arrow pointing to the trigger
  showArrow?: boolean;

  // Whether to close on item click
  closeOnItemClick?: boolean;

  // Whether to display backdrop (mostly for mobile)
  showBackdrop?: boolean;

  // Custom anchor element to position relative to (instead of trigger)
  anchorElement?: HTMLElement | null;

  // Vertical position for mobile (0-1, percentage from top)
  mobilePosition?: number;

  // Title for mobile fullscreen modal
  mobileTitle?: string;

  // Children components
  children: React.ReactNode;
}
